import React from 'react';
import { createLogger } from '../utils/logger';

const logger = createLogger('ErrorBoundary');

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null, errorInfo: null };
	}

	static getDerivedStateFromError(error) {
		// Update state so the next render will show the fallback UI
		return { hasError: true };
	}

	componentDidCatch(error, errorInfo) {
		// Log the error to an error reporting service
		logger.error('ErrorBoundary caught an error:', error);
		logger.error('Error info:', errorInfo);
		
		this.setState({
			error,
			errorInfo
		});
	}

	handleReset = () => {
		this.setState({ hasError: false, error: null, errorInfo: null });
	};

	render() {
		if (this.state.hasError) {
			// Custom fallback UI with fallback colors in case CSS variables aren't loaded
			return (
				<div style={{
					minHeight: '100vh',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					padding: '20px',
					background: 'var(--bg, #0f1419)',
					color: 'var(--text, #e6edf3)'
				}}>
					<div style={{
						maxWidth: '600px',
						width: '100%',
						background: 'var(--panel, #1a1f2e)',
						border: '1px solid var(--border, #2a3142)',
						borderRadius: '12px',
						padding: '32px',
						textAlign: 'center'
					}}>
						<div style={{
							fontSize: '48px',
							marginBottom: '16px'
						}}>
							⚠️
						</div>
						<h1 style={{
							fontSize: '24px',
							fontWeight: 600,
							marginBottom: '12px',
							color: 'var(--text, #e6edf3)'
						}}>
							Something went wrong
						</h1>
						<p style={{
							fontSize: '14px',
							color: 'var(--text-muted, #8b95a6)',
							marginBottom: '24px',
							lineHeight: '1.5'
						}}>
							We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
						</p>
						<div style={{
							display: 'flex',
							gap: '12px',
							justifyContent: 'center',
							flexWrap: 'wrap'
						}}>
							<button
								onClick={this.handleReset}
								style={{
									padding: '10px 20px',
									background: 'var(--accent, #8b5cf6)',
									color: 'white',
									border: 'none',
									borderRadius: '8px',
									fontSize: '14px',
									fontWeight: 600,
									cursor: 'pointer',
									fontFamily: 'inherit'
								}}
								type="button"
								aria-label="Try again"
							>
								Try again
							</button>
							<button
								onClick={() => window.location.reload()}
								style={{
									padding: '10px 20px',
									background: 'transparent',
									color: 'var(--text, #e6edf3)',
									border: '1px solid var(--border, #2a3142)',
									borderRadius: '8px',
									fontSize: '14px',
									fontWeight: 600,
									cursor: 'pointer',
									fontFamily: 'inherit'
								}}
								type="button"
								aria-label="Reload page"
							>
								Reload page
							</button>
						</div>
						{process.env.NODE_ENV === 'development' && this.state.error && (
							<details style={{
								marginTop: '24px',
								padding: '16px',
								background: 'var(--bg, #0f1419)',
								border: '1px solid var(--border, #2a3142)',
								borderRadius: '8px',
								textAlign: 'left'
							}}>
								<summary style={{
									cursor: 'pointer',
									fontSize: '12px',
									fontWeight: 600,
									color: 'var(--text-muted, #8b95a6)',
									marginBottom: '12px'
								}}>
									Error details (development only)
								</summary>
								<pre style={{
									fontSize: '11px',
									color: 'var(--text, #e6edf3)',
									overflow: 'auto',
									maxHeight: '300px',
									margin: 0,
									whiteSpace: 'pre-wrap',
									wordBreak: 'break-word'
								}}>
									{this.state.error.toString()}
									{this.state.errorInfo && (
										<>
											{'\n\n'}
											{this.state.errorInfo.componentStack}
										</>
									)}
								</pre>
							</details>
						)}
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;

